import { useState } from 'react';
import { motion } from 'framer-motion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { BookOpen } from 'lucide-react';
import { getFullChart, ALL_KEYS } from '../lib/nashville_theory';
import { cn } from '../lib/utils';

const ReferenceChart = ({ trigger }) => {
  const [selectedKey, setSelectedKey] = useState('C');
  const chart = getFullChart(selectedKey);

  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" className="btn-press" data-testid="reference-chart-btn">
            <BookOpen className="w-4 h-4 mr-2" />
            Reference Chart
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid="reference-chart-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-3">
            <BookOpen className="w-6 h-6" />
            Number System Chart
          </DialogTitle>
        </DialogHeader>
        
        <div className="mt-4">
          <div className="flex items-center gap-4 mb-6">
            <span className="text-sm text-muted-foreground">Select Key:</span>
            <Select value={selectedKey} onValueChange={setSelectedKey}>
              <SelectTrigger className="w-40" data-testid="key-select">
                <SelectValue placeholder="Select key" />
              </SelectTrigger>
              <SelectContent>
                {ALL_KEYS.map(key => (
                  <SelectItem key={key.name} value={key.name}>
                    {key.display}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse" data-testid="chord-table">
              <thead>
                <tr className="border-b-2 border-border">
                  <th className="py-3 px-4 text-left text-xs uppercase tracking-widest text-muted-foreground font-medium">
                    Number
                  </th>
                  <th className="py-3 px-4 text-left text-xs uppercase tracking-widest text-muted-foreground font-medium">
                    Roman
                  </th>
                  <th className="py-3 px-4 text-left text-xs uppercase tracking-widest text-muted-foreground font-medium">
                    Chord
                  </th>
                  <th className="py-3 px-4 text-left text-xs uppercase tracking-widest text-muted-foreground font-medium">
                    Quality
                  </th>
                </tr>
              </thead>
              <tbody>
                {chart.map((row, index) => (
                  <motion.tr
                    key={row.number}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={cn(
                      "border-b border-border/50",
                      index % 2 === 0 ? "bg-transparent" : "bg-secondary/30"
                    )}
                    data-testid={`chart-row-${row.number}`}
                  >
                    <td className="py-4 px-4">
                      <span className="font-mono text-2xl font-bold text-foreground">
                        {row.number}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="font-mono text-lg text-muted-foreground">
                        {row.roman}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="font-mono text-xl font-semibold text-foreground">
                        {row.chordName}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className={cn(
                        "text-sm px-2 py-1 rounded-full",
                        row.quality === 'maj' && "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300",
                        row.quality === 'min' && "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
                        row.quality === 'dim' && "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300"
                      )}>
                        {row.quality}
                      </span>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 p-4 bg-secondary/50 rounded-xl text-sm text-muted-foreground">
            <p className="font-semibold text-foreground mb-2">Quick Reference:</p>
            <ul className="space-y-1">
              <li>• <strong>1, 4, 5</strong> are always Major chords</li>
              <li>• <strong>2, 3, 6</strong> are always Minor chords</li>
              <li>• <strong>7</strong> is always Diminished</li>
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReferenceChart;
